import React, {useEffect, useMemo, useState} from 'react';
import {useSearchParams} from "react-router";
import {APIProvider, Map} from "@vis.gl/react-google-maps";
import projectUtils from "../../../utils/project-utils.js";
import axios from "axios";
import MapComponent from "./map-component.jsx";
import {useQuery} from "@tanstack/react-query";
import CapturePlacesService from "../../../services/web/capture-places-service.js";
import CorporationService from "../../../services/web/corporation-service.js";
import {Template} from "../../../components/template/template.jsx";
import Button from "../../../components/button/button.jsx";

const API_URL = import.meta.env.VITE_API_URL;
const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const MapSelection = () => {
    const [markersSelected, setMarkersSelected] = useState([]);
    const [canOpenScreen, setCanOpenScreen] = useState(false);

    const [search, setSearch] = useState("");
    const [isPclSelection, setIsPclSelection] = useState(false)
    const [position, setCurrentPosition] = useState(null)
    const [pclMarkers, setPclMarkers] = useState([]);
    const [zoom, setZoom] = useState(14);
    const [bounds, setBounds] = useState(null);

    const [searchParams] = useSearchParams();
    let selectPCLs = searchParams.get('selectPcls');
    const screen = searchParams.get('screen');
    const screenId = searchParams.get('screen_id');
    const occId = searchParams.get('occ_id');
    const address = searchParams.get('address');
    const routes = searchParams.get('routes');

    const [eventCreated, setEventCreated] = useState(false);
    const [addressSearch, setAddressSearch] = useState(null);
    const [markers, setMarkers] = useState([]);

    const { data: capturePlacesData } = useQuery({
        queryKey: ["allowedPlaces"],
        queryFn: () => CapturePlacesService.listAllowed()
    });

    const { data: entity } = useQuery({
        queryKey: ["entity"],
        queryFn: () => CorporationService.entity()
    });

    const [mapState, setMapState] = useState({
        neighborhoodStreets: false,
        pointsOfInterest: false
    });

    const capturePlaces = useMemo(() => {
        if (!routes?.length || !capturePlacesData?.length) return capturePlacesData ?? [];

        return capturePlacesData.filter(item => {
            if (!Array.isArray(item.routes)) return false;

            return item.routes.some(route =>
                routes.includes(route.regionType)
            );
        });
    }, [capturePlacesData, routes]);

    let latitude = entity?.latitude ?? null;
    let longitude = entity?.longitude ?? null;

    useEffect(() => {

        const checkScreenId = async () => {
            await axios.get(`${API_KEEPER}/singleton/screen/${screenId}`)
                .then((response) => {

                    if (response.status === 200) setCanOpenScreen(true);

                })
                .catch(error => {
                    if (error.response.status === 404) projectUtils.swalMessage('error', '', 'Para visualizar o mapa, abra o Sentry View e refaça o procedimento.', 'Ok');
                });
        }

        checkScreenId();
    }, []);

    const sendToSentryView = async () => {

        if (isPclSelection) {
            const map = markersSelected.map(i => i.routes).flat().map(i => i.lanes).flat().map(i => i.code);

            await axios.post(`${API_URL}/map-selection/sendPclLog?screen=${screen}&screen_id=${screenId}`, {pcls: map})
                .then(() => {
                    closeWindow();
                })
                .catch(() => {
                    projectUtils.swalErrorMessage("", "Erro ao selecionar os pontos de captura", "Ok", false, false, () => {})
                });

            return
        }

        await axios.post(`${API_URL}/map-selection/sendOccurrenceLog`, {markersSelected, frmOccId: screenId} )
            .then(() => {
                closeWindow();
            })
            .catch(() => {
                projectUtils.swalErrorMessage("", "Erro ao selecionar o endereço", true, false, false, () => {})
            });
    }

    const closeWindow = () => {
        window.close();
    }

    function geocodeAddress(occurrenceAddress) {
        const geocoder = new window.google.maps.Geocoder();

        return new Promise((resolve, reject) => {
            geocoder.geocode({ address: occurrenceAddress }, (results, status) => {
                if (status === 'OK' && results.length > 0) {
                    const location = results[0].geometry.location;

                    resolve({
                        lat: location.lat(),
                        lng: location.lng(),
                    });
                } else {
                    reject(`Geocode falhou: ${status}`);
                }
            });
        });
    }

    const fillMarkers = async (fillAddress) => {
        const geocoder = new window.google.maps.Geocoder();
        const markers = markersSelected;

        for (const item of markers) {
            item.description = `Latitude: ${Number(item.lat)} \n Longitude: ${Number(item.lng)}`;
            item.address = null;

            if (fillAddress){
                await new Promise((resolve) => {
                    geocoder.geocode(
                        { 'location': { lat: Number(item.lat), lng: Number(item.lng) } },
                        (results, status) => {
                            if (status === 'OK' && !results[0].formatted_address.includes("+")) {
                                item.description = results[0].formatted_address;
                                item.address = results[0].address_components;
                            }
                            resolve();
                        }
                    );
                });
            }

        }

        setMarkersSelected(markers);
    };

    const handleConfirmBtn = async (fillAddress) => {
        await fillMarkers(fillAddress);

        if (isPclSelection){
            await sendToSentryView();
            return;
        }

        projectUtils.swalMessage('info', 'Endereço', `Deseja utilizar: ${markersSelected[0].description}?`, 'Sim', true, 'Não', (result) => { if (result.isConfirmed) sendToSentryView(); });
    }

    const itemClicked = (marker) => {
        const clickedMarker = pclMarkers.find(i => i.code === marker.code);
        const focused = clickedMarker?.bounce;

        setPclMarkers(current =>
            current.map(i => ({
                ...i,
                bounce: focused ? false : i.code === marker.code
            }))
        );

        if (focused) return;
        const bounds = new google.maps.LatLngBounds();

        bounds.extend({
            lat: Number(marker.lat),
            lng: Number(marker.lng)
        });

        setBounds(bounds);
    };

    const handleRemoveAll = () => {
        setMarkersSelected([]);

        setPclMarkers(current =>
            current.map(item => ({
                ...item,
                canAdd: true,
                bounce: false
            }))
        );
    };

    const handleRemoveSelectedMarker = (code) => {
        setMarkersSelected(current =>
            current.filter(item => item.code !== code)
        );

        setPclMarkers(current =>
            current.map(item => ({
                ...item,
                canAdd: item.code === code ? true : item.canAdd,
                bounce: false
            }))
        );
    };

    let handleAddMarker = (marker, type) => {

        if (isPclSelection && type === "pcl") {
            setMarkersSelected(current => {
                const alreadyExists = current.some(obj => obj.code === marker.code);
                if (alreadyExists) return current;

                return [
                    ...current,
                    {
                        code: marker.code,
                        type,
                        description: marker.description,
                        lat: Number(marker.latitude),
                        lng: Number(marker.longitude),
                        routes: marker.routes
                    }
                ];
            });

            setPclMarkers(current =>
                current.map(item => ({
                    ...item,
                    canAdd: item.code === marker.code ? false : item.canAdd
                }))
            );

        } else if (type === "user") {

            setMarkersSelected([
                {
                    type,
                    address: marker.address,
                    description: marker.description,
                    lat: Number(marker.latitude),
                    lng: Number(marker.longitude),
                }
            ]);
        }
    };

    const setDoubleClickEvent = (e, marker)=> {
        if (e == null || eventCreated) return;

        e.addListener("dblclick", (event) => {
            marker.type = "pcl";
            setMarkers([marker]);
        });

        setEventCreated(true);
    }

    useEffect(() => {
        const parse = capturePlaces;
        parse.forEach(item => item.canAdd = true);
        setPclMarkers(parse)
    }, [capturePlaces]);


    useEffect(() => {
        setIsPclSelection(selectPCLs === "true")
    }, [selectPCLs]);

    useEffect(() => {
        if (markers.length  === 0) return;

        markers.forEach((marker) => {
            handleAddMarker(marker, marker.type);
        })

        setMarkers([]);
    }, [markers]);

    const handleMapClick = (event) => {
        if (isPclSelection) return;

        const latitude = event.detail.latLng.lat;
        const longitude = event.detail.latLng.lng;

        handleAddMarker({
            latitude,
            longitude,
        }, "user");

        setCurrentPosition({
            lat: latitude,
            lng: longitude,
        });
    };

    const mapHeight = isPclSelection ? "calc(100vh - 53px)" : "calc(100vh - 103px)";

    return (
        <>
            <Template className={'min-h-[calc(100vh-160px)]'}>
                <Template.Header>
                    <div className="flex justify-between w-full">
                        <Template.Title>{isPclSelection ? "Seleção de ponto de coleta para pesquisa de passagem" : `Seleção do local da ocorrência ${occId ? occId : ""}`}</Template.Title>
                    </div>
                </Template.Header>
                <Template.Body className={'!p-0'}>

                    { canOpenScreen && (

                        <div>
                            <div className="flex h-full w-full">
                                <div className={`flex-grow-1 flex ${!isPclSelection ? "flex-column" : "" }`}>
                                    <div tabIndex={0} className="maps d-flex" style={{height: mapHeight, width: "100%"}}>
                                        { entity && (
                                            <APIProvider apiKey={entity?.maps_api_key ?? ""} language="pt-BR" libraries={["places", "drawing"]}>
                                                <Map
                                                    reuseMaps={true}
                                                    defaultCenter={entity ? {lat: Number(entity.latitude), lng: Number(entity.longitude)} : undefined}
                                                    defaultZoom={zoom}
                                                    gestureHandling="greedy"
                                                    streetViewControl={false}
                                                    fullscreenControl={false}
                                                    mapTypeControl={false}
                                                    zoomControl={false}
                                                    style={{height: "100%"}}
                                                    onClick={handleMapClick}
                                                >

                                                    <MapComponent
                                                        bounds={bounds}
                                                        zoom={zoom}
                                                        isPclSelection={isPclSelection}
                                                        pclMarkers={pclMarkers}
                                                        markersSelected={markersSelected}
                                                        search={search}
                                                        setSearch={setSearch}
                                                        setBounds={setBounds}
                                                        setZoom={setZoom}
                                                        latitude={latitude}
                                                        longitude={longitude}
                                                        address={address}
                                                        addressSearch={addressSearch}
                                                        geocodeAddress={geocodeAddress}
                                                        handleAddMarker={handleAddMarker}
                                                        setCurrentPosition={setCurrentPosition}
                                                        setMarkers={setMarkers}
                                                        mapState={mapState}
                                                        setMapState={setMapState}
                                                        itemClicked={itemClicked}
                                                        position={position}
                                                        handleRemoveAll={handleRemoveAll}
                                                        sendToSentryView={sendToSentryView}
                                                        handleRemoveSelectedMarker={handleRemoveSelectedMarker}
                                                        setDoubleClickEvent={setDoubleClickEvent}
                                                        setAddressSearch={setAddressSearch}
                                                        handleMapClick={handleMapClick}
                                                        closeWindow={closeWindow}
                                                        handleConfirmBtn={handleConfirmBtn}
                                                    />

                                                </Map>
                                            </APIProvider>
                                        )}

                                        { !isPclSelection && (
                                            <>

                                                <div className="flex flex-row gap-2 w-full! justify-end p-2 border border-t-[#e7e7e7]">
                                                    <Button target="danger" onClick={closeWindow} disabled={markersSelected.length === 0}>Cancelar</Button>
                                                    <Button onClick={async () => {await handleConfirmBtn(false)}} disabled={markersSelected.length === 0}>Confirmar localização</Button>
                                                    <Button onClick={async () => {await handleConfirmBtn(true)}} disabled={markersSelected.length === 0}>Confirmar localização e endereço</Button>
                                                </div>

                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </Template.Body>
            </Template>
        </>

    );
};

export default MapSelection;